import React, { FC, useState } from "react"
import TextField from "@mui/material/TextField"
import Chip from "@mui/material/Chip"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import SectionWrapper, { SectionStatus } from "./LockedSectionWrapper"
import VideoPlayer from "../../player/VideoPlayer"
import { Participant, Video } from "../../types"
import { isLikelyHlsUrl } from "./videoFields"

interface StreamUrlsSectionProps {
  video: Video
  streamUrl: string
  onStreamUrlChange: (url: string) => void
  onCommit: () => void
  // player preview props
  subtitleContentUrl?: string
  participants?: Participant[]
  attributions?: Record<string, string>
  subtitleOffset?: number
  rotationAdjustment?: number
}

const StreamUrlsSection: FC<StreamUrlsSectionProps> = ({
  video,
  streamUrl,
  onStreamUrlChange,
  onCommit,
  subtitleContentUrl,
  participants,
  attributions,
  subtitleOffset,
  rotationAdjustment,
}) => {
  const [preview, setPreview] = useState(false)

  const status: SectionStatus = video.state === "ready" ? "complete" : "pending"
  // Stream URLs are placement, not the video itself — the manifest names no
  // host precisely so hosting can move forever, even after sealing. The one
  // window they're locked is while a publish job is actually enumerating the
  // current playlist: repointing underneath it would seal the wrong bytes.
  // Matches guardStreamURLs on the server exactly — nothing else locks these.
  const hashing = video.publication_state === "publishing"

  return (
    <SectionWrapper
      title="Stream"
      status={status}
      actions={
        <>
          <Chip
            label={video.state === "ready" ? "Ready" : "Awaiting stream URL"}
            color={video.state === "ready" ? "success" : "default"}
            size="small"
          />
          <Button
            size="small"
            variant="outlined"
            disabled={!video.stream_url}
            onClick={() => setPreview(true)}
          >
            Preview player
          </Button>
        </>
      }
    >
      <TextField
        fullWidth
        label="360° stream URL (HLS)"
        placeholder="https://your-zone.b-cdn.net/{video-id}/playlist.m3u8"
        value={streamUrl}
        onChange={(e) => onStreamUrlChange(e.target.value)}
        onBlur={() => onCommit()}
        disabled={hashing}
        error={!hashing && !isLikelyHlsUrl(streamUrl)}
        helperText={
          hashing
            ? "Locked while the publish job is hashing this exact playlist. Editable again once it finishes."
            : !isLikelyHlsUrl(streamUrl)
              ? "Expected an https URL containing .m3u8"
              : "Paste the adaptive stream URL from Bunny.net. Hosting can change any time, including after this video is sealed."
        }
      />

      <Dialog open={preview} onClose={() => setPreview(false)} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 1 }}>
          {video.stream_url && (
            <VideoPlayer
              src={video.stream_url}
              type="application/x-mpegURL"
              participants={participants || []}
              subtitleUrl={subtitleContentUrl}
              subtitleOffset={subtitleOffset || 0}
              speakerAttributions={attributions || {}}
              rotationAdjustment={rotationAdjustment}
              fourView={video.four_view}
              subtitleLanguages={video.subtitles?.languages}
              masterLanguage={video.subtitles?.master_language}
              compact
            />
          )}
        </DialogContent>
      </Dialog>
    </SectionWrapper>
  )
}

export default StreamUrlsSection
